/*
 * ******************************************************************
 * Copyright (c) 2024 Broadcom. All Rights Reserved.
 * Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
 * and/or its subsidiaries.
 * ******************************************************************
 */
import { Directive, Injector, Input, OnDestroy, OnInit } from "@angular/core";
import { ReplaySubject, Subject } from "rxjs";
import { I18nService, L10nService } from "@vmw/ngx-vip";
import { Dispatcher } from "@app/services/dispatcher/dispatcher";
import { LocalStorageService } from "@app/services/local-storage/local-storage.service";
import { RbacService } from "@shared/services";
import { ConsoleType } from "@shared/models/global.model";
import { AppConfigService, AppStateService, ErrorService, TimerService } from "@app/services";
import { ActivatedRoute } from "@angular/router";
import { BreadcrumbService } from "@shared/components/breadcrumb/breadcrumb.service";
import { ContextService } from "@shared/services/context/context.service";
import { DialogService } from "@shared/components/dialog/dialog.service";
import { StatesUtil } from "@shared/utils/states.util";

@Directive()
export abstract class BaseComponent implements OnInit, OnDestroy {

    ConsoleType = ConsoleType;
    StatesUtil = StatesUtil;

    public loading: boolean = false;
    public errorMsg: string;
    public undefinedString: string;
    breadcrumbsEnabled: boolean = false;

    @Input() contextService: ContextService;

    dispatcher: Dispatcher;
    l10nService: L10nService;
    localStorage: LocalStorageService;
    appConfigService: AppConfigService;
    errorService: ErrorService;
    rbacService: RbacService;
    route: ActivatedRoute;
    breadcrumbService: BreadcrumbService;
    i18nService: I18nService;
    appStateService: AppStateService;
    dialogService: DialogService;
    timerService: TimerService;

    protected unsubscribeSubject: Subject<void> = new ReplaySubject<void>(1);

    constructor(injector: Injector) {
        this.dispatcher = injector.get<Dispatcher>(Dispatcher);
        this.l10nService = injector.get<L10nService>(L10nService);
        this.localStorage = injector.get<LocalStorageService>(LocalStorageService);
        this.appConfigService = injector.get<AppConfigService>(AppConfigService);
        this.errorService = injector.get<ErrorService>(ErrorService);
        this.rbacService = injector.get<RbacService>(RbacService);
        this.route = injector.get<ActivatedRoute>(ActivatedRoute);
        this.breadcrumbService = injector.get<BreadcrumbService>(BreadcrumbService);
        this.i18nService = injector.get<I18nService>(I18nService);
        this.appStateService = injector.get<AppStateService>(AppStateService);
        this.dialogService = injector.get<DialogService>(DialogService);
        this.timerService = injector.get<TimerService>(TimerService);

        this.undefinedString = this.l10nService.getMessage("general.UNDEFINED_STRING");
    }

    ngOnInit(): void {
        this.errorMsg = null;
        if (this.breadcrumbsEnabled) {
            this.breadcrumbService.init(this.route, this.contextService);
        }
        this.init();
    }

    ngOnDestroy(): void {
        this.unsubscribeSubject.next();
        this.unsubscribeSubject.complete();
        if (this.breadcrumbsEnabled) {
            this.breadcrumbService.destroy();
        }
        this.destroy();
    }

    protected init(): void {
        // Do nothing by default
    }

    protected destroy(): void {
        // Do nothing by default
    }
}
